import { ChangeEvent, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { faHandshake } from '@fortawesome/pro-solid-svg-icons/faHandshake';

import { Include, IncludeService } from 'us-web-services';

import { Checkbox } from 'us-react-components/dist/Checkbox/Checkbox';
import { InfoCard } from 'us-react-components/dist/InfoCard/InfoCard';
import { LogoSpinner } from 'us-react-components/dist/LogoSpinner/LogoSpinner';
import { NuxContainer } from 'us-react-components/dist/NuxContainer/NuxContainer';
import { Routes } from '../../../Routes';
import { useDependencies } from '../../../dependencies';

const NONE_OF_THE_ABOVE = 'none';

export const Includes = observer((): JSX.Element => {
  const [loading, setLoading] = useState(true);
  const [noneOfTheAbove, setNoneOfTheAbove] = useState(false);
  const [includesAvailable, setIncludesAvailable] = useState<Include[]>([]);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { stores: { familyRegistration }, services: { tracker } } = useDependencies();

  const BATHING_INCLUDE_ID = 5;

  familyRegistration.setCurrentRoute(Routes.parent.registration.includes);

  useEffect(() => {
    const fetchIncludes = async (): Promise<void> => {
      try {
        const includesResponse = (await IncludeService.getBookingIncludes()).data;
        setIncludesAvailable(includesResponse.data.filter((include: Include) => include.id !== BATHING_INCLUDE_ID).reverse());
      } catch (error) {
        console.error('Error fetching includes', error);
      } finally {
        setLoading(false);
      }
    }

    fetchIncludes();
  }, []);

  const handleContinue = (): void => {
    navigate(Routes.parent.registration.sweeteners)
  }

  const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const includes = familyRegistration.includes ?? [];

    let updatedIncludes: Include[] = [...includes];

    if (e.target.id === `include-${NONE_OF_THE_ABOVE}`) {
      updatedIncludes = [];
      setNoneOfTheAbove(!noneOfTheAbove);
    } else {
      const includeId = Number.parseInt(e.target.id.substr(8), 10);
      const selectedInclude = includesAvailable.find(i => i.id === includeId);

      if (includes.some(i => i.id === includeId)) {
        updatedIncludes = includes.filter(i => i.id !== includeId);
      } else if (selectedInclude) {
        updatedIncludes.push(selectedInclude);
      }
      setNoneOfTheAbove(false);
    }

    familyRegistration.setIncludes(updatedIncludes);
  }

  const renderInfoCard = (): JSX.Element => (
    <InfoCard
      iconName={faHandshake}
      title={t('pages.nux.family.includes.infoCard.title')}
      description={t('pages.nux.family.includes.infoCard.description')}
    />
  );

  return (
    <NuxContainer
      familyRegistration={familyRegistration}
      navigate={navigate}
      tracker={tracker}
      infoCard={renderInfoCard()}
      title={t('pages.nux.family.includes.title')}
      onContinue={handleContinue}>
      <div className='flex w-[78%] sm:w-[70%] flex-col py-8'>
        <p className='text-xl font-semibold pb-4'>{t('pages.nux.family.includes.jobIncludes')}</p>
        {loading ? <LogoSpinner /> :
          <>
            {includesAvailable.map((include: Include) => (
              <Checkbox
                checked={familyRegistration.includes?.some(i => i.id === include.id) ?? false}
                className='pb-2'
                id={`include-${include.id}`}
                key={include.id}
                label={include.label ?? undefined}
                onChange={handleCheckboxChange}
              />
            ))}
            <Checkbox
              checked={noneOfTheAbove}
              className='pb-4 sm:pb-8'
              id={`include-${NONE_OF_THE_ABOVE}`}
              label={t('pages.nux.family.includes.noneOfTheAbove')}
              onChange={handleCheckboxChange}
            />
          </>}
      </div>
    </NuxContainer>
  )
});
